/**
 * Teclado e foco comuns a todo popup do app — C-91.
 *
 * Regras do dono (18/10/2026):
 * 1. **Esc faz o mesmo que o ×.** Passe em `aoFechar` o MESMO handler do × (num popup
 *    de confirmação, o do "Não"/"Voltar"/"Cancelar").
 * 2. Por isso, **se o × abre uma confirmação, o Esc também abre** — não pula a etapa.
 * 3. **Um Esc fecha só o popup de cima.** Os popups abertos formam uma pilha; o Esc é
 *    consumido por ela e não chega a nenhum outro ouvinte da página.
 *
 * Também: ao abrir, o foco entra no popup; Tab e Shift+Tab não saem dele; ao fechar,
 * o foco volta a quem abriu — se ainda existir, estiver habilitado e o foco tiver
 * ficado sem dono (não tira o foco de quem o pegou nesse meio-tempo).
 *
 * Uso: `const ref = usePopup<HTMLDivElement>({ aoFechar: onClose })` e
 * `<div ref={ref} role="dialog" aria-modal="true" tabIndex={-1}>`. A guarda
 * `estrutura/popup.guard.test.ts` exige o hook em todo arquivo com `role="dialog"`.
 *
 * Por consumir o Esc numa escuta de captura da `window`, um `onKeyDown` de Esc
 * dentro do popup nunca é chamado — o fechamento é sempre o do `aoFechar`.
 */
import { useEffect, useRef, type RefObject } from 'react';

export interface OpcoesPopup {
  /** O mesmo handler do × (ou do "Não"/"Voltar" de uma confirmação). */
  aoFechar: () => void;
  /** Para popup que fica montado e só alterna a visibilidade. Padrão: `true`. */
  aberto?: boolean;
  /** Enquanto o × está travado (ex.: salvando), o Esc é ignorado — e não passa adiante. */
  bloqueado?: boolean;
  /** Tab circula só dentro do popup. Padrão: `true`; `false` para menus suspensos. */
  prenderFoco?: boolean;
  /** Foca o popup ao abrir e devolve o foco a quem abriu. Padrão: `true`. */
  gerirFoco?: boolean;
}

interface Entrada {
  aoFechar: () => void;
  bloqueado: () => boolean;
  conteiner: () => HTMLElement | null;
  prenderFoco: boolean;
}

/** Popups abertos, na ordem em que abriram: o último é o de cima. */
const pilha: Entrada[] = [];

const FOCAVEIS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Último elemento acionado com o ponteiro. Safari e Firefox do macOS não focam o
 * botão no clique do mouse: sem isto, a origem do popup seria o `body` e o foco não
 * voltaria ao botão que o abriu.
 */
let ultimoAcionado: HTMLElement | null = null;
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    (e) => {
      const alvo = e.target instanceof Element ? e.target.closest<HTMLElement>(FOCAVEIS) : null;
      ultimoAcionado = alvo;
    },
    true,
  );
}

function quemTemOFoco(): HTMLElement | null {
  const ativo = document.activeElement;
  if (ativo instanceof HTMLElement && ativo !== document.body) return ativo;
  return ultimoAcionado?.isConnected ? ultimoAcionado : null;
}

function prenderTab(e: KeyboardEvent, conteiner: HTMLElement | null) {
  if (!conteiner) return;
  const focaveis = Array.from(conteiner.querySelectorAll<HTMLElement>(FOCAVEIS));
  const primeiro = focaveis[0];
  const ultimo = focaveis[focaveis.length - 1];
  if (!primeiro || !ultimo) {
    e.preventDefault();
    conteiner.focus();
    return;
  }
  const atual = document.activeElement;
  const dentro = atual instanceof Node && conteiner.contains(atual);
  const sai = e.shiftKey ? !dentro || atual === primeiro || atual === conteiner : !dentro || atual === ultimo;
  if (sai) {
    e.preventDefault();
    (e.shiftKey ? ultimo : primeiro).focus();
  }
}

function aoTeclar(e: KeyboardEvent) {
  const topo = pilha[pilha.length - 1];
  if (!topo) return;
  if (e.key === 'Escape') {
    // Regra 3: o Esc é do popup de cima. Nenhum outro ouvinte (outro popup, menu,
    // atalho da página) fecha junto — nem quando o de cima está bloqueado.
    e.stopPropagation();
    // Esc segurado repete o keydown: o 2º encontraria o popup de baixo no topo e o
    // fecharia também (ex.: "Não" da confirmação e, em seguida, o formulário).
    if (e.repeat) return;
    if (!topo.bloqueado()) topo.aoFechar();
    return;
  }
  if (e.key === 'Tab' && topo.prenderFoco) prenderTab(e, topo.conteiner());
}

function empilhar(entrada: Entrada) {
  if (pilha.length === 0) window.addEventListener('keydown', aoTeclar, true);
  pilha.push(entrada);
}

function desempilhar(entrada: Entrada) {
  const i = pilha.indexOf(entrada);
  if (i >= 0) pilha.splice(i, 1);
  if (pilha.length === 0) window.removeEventListener('keydown', aoTeclar, true);
}

export function usePopup<T extends HTMLElement = HTMLDivElement>({
  aoFechar,
  aberto = true,
  bloqueado = false,
  prenderFoco = true,
  gerirFoco = true,
}: OpcoesPopup): RefObject<T> {
  const ref = useRef<T>(null);
  // Ref: o `aoFechar` inline do pai muda a cada render e não deve reempilhar o popup.
  const atual = useRef({ aoFechar, bloqueado });
  atual.current = { aoFechar, bloqueado };

  // Quem tinha o foco quando o popup abriu. Lido no render — antes de um `autoFocus`
  // de dentro do popup levar o foco embora.
  const origem = useRef<HTMLElement | null>(null);
  const estavaAberto = useRef(false);
  if (aberto && !estavaAberto.current) origem.current = quemTemOFoco();
  estavaAberto.current = aberto;

  const montado = useRef(false);
  useEffect(() => {
    if (!aberto) return;
    montado.current = true;
    const entrada: Entrada = {
      aoFechar: () => atual.current.aoFechar(),
      bloqueado: () => atual.current.bloqueado,
      conteiner: () => ref.current,
      prenderFoco,
    };
    empilhar(entrada);
    const conteiner = ref.current;
    if (gerirFoco && conteiner && !conteiner.contains(document.activeElement)) conteiner.focus();
    const quemAbriu = origem.current;
    return () => {
      montado.current = false;
      desempilhar(entrada);
      if (!gerirFoco || !quemAbriu) return;
      // Microtarefa: no StrictMode (dev) o efeito desmonta e remonta na hora — aí o
      // popup continua aberto e o foco fica nele.
      queueMicrotask(() => {
        if (montado.current) return;
        // Só devolve o foco que ficou sem dono (estava no popup, que sumiu).
        const ativo = document.activeElement;
        if (ativo && ativo !== document.body) return;
        if (quemAbriu.isConnected && !quemAbriu.matches(':disabled')) quemAbriu.focus();
      });
    };
  }, [aberto, prenderFoco, gerirFoco]);

  return ref;
}
